import validator from 'validate.js';
import { PanelData } from '../@types/panel-data';
import { isFormInputPanel } from './panel/form-input-panel';

interface Ruleset {
  [field: string]: { [rule: string]: any } | ((input: any, fields: any) => { [rule: string]: any });
}

/**
 * A phone validator that asynchronously loads libphonenumber-js at run-time and uses
 * it to validate the user's phone number.
 */
validator.validators.phone = async function (value) {
  if (!value) return;

  const { isValidPhoneNumber } = await import('libphonenumber-js');
  if (!isValidPhoneNumber(value)) {
    return 'is not valid';
  }
};

/**
 * An object holding the validate.js constraint rules for known fields.
 * Each key can be either an object OR a function which will be evaluated at run-time with
 * the current input and full list of fields. (Useful for countryCode-specific rules.)
 */
const rules: Ruleset = {
  email: {
    email: true,
  },

  full_name: {
    format: {
      flags: 'i',
      message: 'must have first and last name',
      pattern: '^([a-z\u00C0-\u017F]{2,})( ([a-z\u00C0-\u017F-]{2,})){1,2}?( (Jr.|Sr.))?$',
    },
    length: {
      minimum: 6,
      tooShort: 'is too short',
    },
  },

  zip_code: (_, formData) => {
    // TODO: Should we use https://www.npmjs.com/package/postcode-validator ?
    const zipcodes = {
      CA: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
      GB: new RegExp(
        '^([Gg][Ii][Rr] 0[Aa]{2})|' +
          '((([A-Za-z][0-9]{1,2})|' +
          '(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|' +
          '(([A-Za-z][0-9][A-Za-z])|' +
          '([A-Za-z][A-Ha-hJ-Yj-y][0-9]?' +
          '[A-Za-z]))))[ ]{0,1}[0-9][A-Za-z]{2})$'
      ),
      US: /^[0-9]{5}(?:-[0-9]{4})?$/,
    };

    const countryZipCodeRegexp = zipcodes[formData.country_code] ?? zipcodes.US;

    return {
      format: {
        flags: countryZipCodeRegexp.flags,
        message: 'is not valid',
        pattern: countryZipCodeRegexp.source,
      },
    };
  },

  phone_number: {
    phone: true,
  },
};

function getRule(name, input, formData) {
  if (!rules[name]) return {};
  const rule = rules[name];
  return typeof rule === 'function' ? rule(input, formData) : rule;
}

function cleanInput(input: any) {
  return input === '' ? null : input;
}

/** Performs client-side validation of the current step's fields. */
export default async function validate(formData: {}, currentStep: PanelData) {
  if (isFormInputPanel(currentStep)) {
    const { inputFields } = currentStep;

    // construct an object of the input fields we want to validate
    const attributes = Object.fromEntries(
      inputFields.map(({ name, type }) => [name || type, cleanInput(formData[name || type])])
    );

    // construct an object of our validation constraints for each input field,
    // based on the validate.js rules object defined at the top of this file
    const constraints = Object.fromEntries(
      inputFields.map((input) => {
        const { name, type, required } = input;

        return [
          name || type,
          {
            presence: required ? { allowEmpty: false, message: 'is required' } : false,
            ...getRule(type, input, formData),
            ...getRule(name, input, formData),
          },
        ];
      })
    );

    // call the async validator which will reject the promise in the event of validation failure
    await validator.async(attributes, constraints);
  }
}
